import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { EmptyState, Spinner, StatusBadge, fmtDay, inputCls } from "@/components/portal/shared";

export default function CustomersPage() {
  const { api } = usePortalAuth();
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");

  const load = (query = q) =>
    api.get("/leads", { params: { q: query } }).then((r) => setLeads(r.data.leads)).catch(() => setLeads([]));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (leads === null) return <Spinner />;

  return (
    <div data-testid="customers-page" className="space-y-5">
      <div className="relative max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid="customers-search"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder="Search customers by name, ref, postcode…"
          className={`${inputCls} pl-10 rounded-full`}
        />
      </div>

      {leads.length === 0 ? (
        <EmptyState icon={Users}>No customer records match.</EmptyState>
      ) : (
        <div className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-warm-200 bg-warm-100/60">
                <th className="px-5 py-3">Ref</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3">Since</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-200">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-warm-100/50 transition-colors" data-testid={`customer-row-${l.id}`}>
                  <td className="px-5 py-3.5 text-xs font-semibold text-slate-500">{l.cust_ref}</td>
                  <td className="px-4 py-3.5">
                    <Link to={`/portal/customers/${l.id}`} data-testid={`customer-open-${l.id}`} className="font-semibold text-navy-900 hover:underline">
                      {l.firstName} {l.lastName}
                    </Link>
                    <span className="block text-xs text-slate-400">{l.email || "—"}</span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-slate-600">{[l.address1, l.town, l.postcode].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-600">{l.phone || "—"}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-600">{l.assigned_to || "—"}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-400">{fmtDay(l.created_at)}</td>
                  <td className="px-4 py-3.5"><StatusBadge s={l.status} simple /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
